import React, { createContext, useContext, useState, useCallback } from 'react'
import en from './en'

// Supported languages. 'ru' is the source language (strings live in the code as Russian),
// so translating just means mapping a Russian string to its English equivalent.
export const LANGUAGES = [
  { value: 'ru', label: 'РУС' },
  { value: 'en', label: 'ENG' },
]
const DEFAULT_LANG = 'ru'
const COOKIE = 'sbx_lang'

// Module-level current language so the plain `t()` helper works anywhere without a hook.
// The provider remounts the tree on change (key={lang}) so every t() re-evaluates.
let currentLang = DEFAULT_LANG

function readCookie() {
  if (typeof document === 'undefined') return DEFAULT_LANG
  const m = document.cookie.match(/(?:^|; )sbx_lang=([^;]+)/)
  const v = m ? decodeURIComponent(m[1]) : DEFAULT_LANG
  return LANGUAGES.some((l) => l.value === v) ? v : DEFAULT_LANG
}

function writeCookie(lang) {
  if (typeof document === 'undefined') return
  document.cookie = `${COOKIE}=${encodeURIComponent(lang)}; path=/; max-age=${60 * 60 * 24 * 365}`
}

export function getLang() {
  return currentLang
}

// Translate a Russian source string to the active language.
// Unknown strings fall back to the Russian original.
export function t(ru) {
  if (currentLang === 'en') return (en && en[ru]) != null ? en[ru] : ru
  return ru
}

const LanguageContext = createContext({ lang: DEFAULT_LANG, setLang: () => {} })

export function useLanguage() {
  return useContext(LanguageContext)
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => readCookie())
  // Keep the module-level value in sync during render so t() is correct for this pass.
  currentLang = lang

  const setLang = useCallback((next) => {
    if (!LANGUAGES.some((l) => l.value === next)) return
    currentLang = next
    writeCookie(next)
    setLangState(next)
  }, [])

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {/* Remount the tree on language change so all t() calls re-render. */}
      <React.Fragment key={lang}>{children}</React.Fragment>
    </LanguageContext.Provider>
  )
}
